// Where a customer's notice stands, and what happened to it, in the customer's own terms.

export type NoticeEvent = { id: string; action: string; actorSubject: string; actorName?: string | null; actorInternal?: boolean | null;
  metadata: Record<string, unknown>; occurredAt: string };
export type StepState = 'done' | 'current' | 'upcoming';
export type ProgressStep = { key: string; label: string; state: StepState; at: string | null; note: string | null };

const sent = ['NOTICE_LETTER_MAILED', 'NOTICE_MAILING_RECORDED', 'NOTICE_FILED'];
const exceptions: Record<string, string> = { VENDOR_EXCEPTION: 'The mailing partner reported a problem; PayRight is on it',
  RETURNED_MAIL: 'A letter came back; PayRight will tell you what to do', RECORDING_REJECTED: 'The filing was rejected; PayRight will tell you what to do',
  CORRECTION: 'PayRight is correcting the notice', COUNSEL_ESCALATION: 'PayRight’s attorney is reviewing it', MISSING_FACTS: 'PayRight needs more job details from you' };

/** The latest time an action happened after another action last happened, so a reopened confirmation no longer counts. */
function latestAfter(events: NoticeEvent[], actions: string[], after: string[] = []): string | null {
  const last = (names: string[]) => events.filter(event => names.includes(event.action)).map(event => event.occurredAt).sort().at(-1) ?? null;
  const done = last(actions); const undone = last(after);
  return done && (!undone || done > undone) ? done : null;
}

/**
 * The notice's lifecycle as the customer sees it: requested, recipients confirmed, prepared, sent, served.
 * The first step not done is the current one, with a note saying whose turn it is.
 */
export function noticeProgress(order: { status: string; nextAction?: string | null; customerCertifiedAt?: string | null; createdAt?: string | null; waitingForRule?: boolean; noticeRequired?: boolean },
  events: NoticeEvent[]): ProgressStep[] {
  const status = order.status;
  const past = (...statuses: string[]) => !statuses.includes(status);
  const requested = status !== 'DRAFT' ? order.customerCertifiedAt ?? latestAfter(events, ['ORDER_CERTIFIED']) : null;
  const confirmed = past('DRAFT', 'MISSING_FACTS', 'RESEARCH') ? latestAfter(events, ['NOTICE_RECIPIENTS_CERTIFIED'], ['NOTICE_RECIPIENTS_REOPENED', 'PROJECT_UPDATED']) ?? 'yes' : null;
  const prepared = confirmed ? latestAfter(events, ['NOTICE_GENERATED'], ['NOTICE_RECIPIENTS_REOPENED', 'PROJECT_UPDATED']) : null;
  const wasSent = ['SUBMITTED', 'FULFILLED'].includes(status) ? latestAfter(events, sent) ?? 'yes' : latestAfter(events, sent);
  const served = status === 'FULFILLED' ? latestAfter(events, ['ORDER_TRANSITIONED']) ?? 'yes' : null;
  const yourTurn = /^(confirm|payright needs you|choose|mail|record|add|pay\b)/i.test(order.nextAction ?? '');
  const steps: Array<[string, string, string | null, string]> = [
    ['requested', 'Requested', requested, order.noticeRequired === false ? 'No notice is needed for this job' : order.waitingForRule ? 'PayRight is finding the rule for this job' : 'Confirm the notice to send it to PayRight'],
    ['recipients', 'Recipients confirmed', confirmed, yourTurn ? 'Your turn: confirm who it goes to' : 'PayRight is searching the public records'],
    ['prepared', 'Notice prepared', prepared, status === 'FIRST_REVIEW' ? 'PayRight is reviewing it' : 'PayRight prepares it'],
    ['sent', 'Sent', wasSent, yourTurn ? 'Your turn: choose how to send it' : 'Waiting to be sent'],
    ['served', 'Served', served, 'Served once it is mailed or filed'],
  ];
  const current = steps.findIndex(([, , at]) => !at);
  return steps.map(([key, label, at, note], index) => ({ key, label, at: at && at !== 'yes' ? at : null,
    state: index < current || current < 0 ? 'done' : index === current ? 'current' : 'upcoming',
    note: index === current ? exceptions[status] ?? note : null }));
}

const research = (action: string) => action.startsWith('RESEARCH_');
const alertKinds: Record<string, string> = { CONFIRM_RECIPIENTS: 'We emailed you to confirm who it goes to',
  RECIPIENTS_NEEDED: 'We emailed you for help with who it goes to', NOTICE_READY: 'We emailed you that it is ready to confirm' };
const transitions: Record<string, string> = { FIRST_REVIEW: 'PayRight started reviewing it', READY_FOR_FULFILLMENT: 'Ready to send',
  SUBMITTED: 'Sent', FULFILLED: 'Served', CORRECTION: 'PayRight started correcting it', CANCELLED: 'Cancelled', RESEARCH: 'Back to checking the details' };
const plain: Record<string, string> = { ORDER_CREATED: 'You started this notice', ORDER_CERTIFIED: 'You sent it to PayRight',
  NOTICE_HELD_FOR_RULE: 'PayRight started finding the rule for this job', NOTICE_RULE_FOUND: 'PayRight found the rule, so the notice has a due date',
  NOTICE_RECIPIENTS_CERTIFIED: 'You confirmed who it goes to', NOTICE_RECIPIENTS_REOPENED: 'You reopened who it goes to, to change it',
  NOTICE_GENERATED: 'PayRight prepared the notice', NOTICE_DELIVERY_CHOSEN: 'You chose how to send it', NOTICE_MAILING_REQUESTED: 'You asked PayRight to mail it',
  NOTICE_LETTER_MAILED: 'PayRight mailed a letter', NOTICE_MAILING_RECORDED: 'You recorded the mailing', NOTICE_FILED: 'PayRight filed it',
  NOTICE_LETTER_FAILED: 'A letter could not be mailed', DOCUMENT_UPLOAD_COMPLETED: 'A file was added' };

export type Moment = { id: string; at: string; text: string };
/**
 * The history a customer reads: what they and PayRight did, newest first. PayRight's research runs
 * collapse into one line per stretch of searching; internal bookkeeping is left to the full record.
 */
export function noticeMoments(events: NoticeEvent[]): Moment[] {
  const moments: Moment[] = [];
  let searches: NoticeEvent[] = [];
  const flush = () => {
    if (!searches.length) return;
    const runs = searches.filter(event => event.action === 'RESEARCH_STARTED').length;
    moments.push({ id: searches[0]!.id, at: searches[0]!.occurredAt, text: `PayRight searched the public records${runs > 1 ? ` (${runs} searches)` : ''}` });
    searches = [];
  };
  for (const event of [...events].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))) {
    if (research(event.action)) { searches.push(event); continue; }
    const text = event.action === 'NOTICE_ALERT_SENT' ? alertKinds[String(event.metadata.kind)] ?? 'We emailed you'
      : event.action === 'ORDER_TRANSITIONED' ? transitions[String(event.metadata.to)] ?? null : plain[event.action] ?? null;
    if (!text) continue;
    flush();
    moments.push({ id: event.id, at: event.occurredAt, text });
  }
  flush();
  return moments;
}
